import React, { useState } from "react";
import styled from "styled-components";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaInstagram,
  FaYoutube,
} from "react-icons/fa";
import { FaTiktok } from "react-icons/fa6";
import {
  colors,
  fonts,
  spacing,
  borderRadius,
  shadows,
  transitions,
} from "../styles/branding";
import { useFormSubmit } from "../hooks/useFormSubmit";

const ContactContainer = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  background: ${colors.gradient.primary};
  color: white;
  padding: 4rem 2rem;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  font-family: ${fonts.heading};
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  font-family: ${fonts.body};
`;

const ContactSection = styled.section`
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  h2 {
    font-size: 2.5rem;
    margin-bottom: 2rem;
    color: ${colors.inkBlack};
    font-family: ${fonts.heading};
    font-weight: 600;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: ${colors.coolTaupe};
    margin-bottom: 2rem;
    font-family: ${fonts.body};
  }
`;

const ContactMethod = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: ${colors.paleMauve};
  border-radius: ${borderRadius.md};

  .icon {
    font-size: 1.5rem;
    color: ${colors.dustyRose};
  }

  .details {
    h3 {
      font-size: 1.1rem;
      margin-bottom: 0.25rem;
      color: ${colors.inkBlack};
      font-family: ${fonts.body};
      font-weight: 500;
    }

    p {
      color: ${colors.coolTaupe};
      margin: 0;
      font-family: ${fonts.body};
    }
  }
`;

const SocialLinks = styled.div`
  margin-top: 2rem;

  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: ${colors.inkBlack};
    font-family: ${fonts.heading};
    font-weight: 500;
  }

  .social-grid {
    display: flex;
    gap: 1rem;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: ${colors.dustyRose};
  color: white;
  border-radius: 50%;
  font-size: 1.2rem;
  transition: ${transitions.normal};

  &:hover {
    background: ${colors.coolTaupe};
    transform: translateY(-2px);
  }
`;

const ContactForm = styled.form`
  background: white;
  padding: 3rem;
  border-radius: ${borderRadius.md};
  box-shadow: ${shadows.lg};

  h2 {
    font-size: 2rem;
    margin-bottom: 2rem;
    color: ${colors.inkBlack};
    font-family: ${fonts.heading};
    font-weight: 600;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 2rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: bold;
    color: ${colors.inkBlack};
    font-family: ${fonts.body};
  }

  input,
  textarea,
  select {
    width: 100%;
    padding: 1rem;
    border: 2px solid #eee;
    border-radius: 8px;
    font-size: 1rem;
    font-family: ${fonts.body};
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: ${colors.dustyRose};
    }
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }

  @media (max-width: 768px) {
    input,
    textarea,
    select {
      padding: 1.2rem;
      font-size: 1.1rem;
    }
  }
`;

const SubmitButton = styled.button`
  background: ${colors.dustyRose};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: ${borderRadius.lg};
  font-weight: bold;
  font-size: 1rem;
  font-family: ${fonts.body};
  cursor: pointer;
  transition: ${transitions.normal};
  width: 100%;

  &:hover {
    background: ${colors.coolTaupe};
    transform: translateY(-2px);
  }
`;

const ThankYouMessage = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background: ${colors.paleMauve};
  border-radius: ${borderRadius.lg};
  margin-top: 2rem;
`;

const ThankYouTitle = styled.h2`
  font-size: 2rem;
  color: ${colors.inkBlack};
  margin-bottom: 1.5rem;
  font-family: ${fonts.heading};
  font-weight: 600;
`;

const ThankYouText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${colors.coolTaupe};
  margin-bottom: 1.5rem;
  font-family: ${fonts.body};
  font-style: italic;
`;

const BackButton = styled.button`
  background: ${colors.dustyRose};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: ${borderRadius.md};
  font-weight: bold;
  font-family: ${fonts.body};
  cursor: pointer;
  transition: ${transitions.normal};

  &:hover {
    background: ${colors.coolTaupe};
    transform: translateY(-2px);
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [showThankYou, setShowThankYou] = useState(false);

  const { submitForm, isSubmitting, submitStatus, resetStatus } =
    useFormSubmit();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Format the message with subject and message, two newlines between each
    const formattedMessage = `Subject: ${formData.subject}\n\nMessage: ${formData.message}`;

    const result = await submitForm(
      "Contact",
      formData.name,
      formData.email,
      formattedMessage
    );

    if (result.success) {
      setShowThankYou(true);
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
      resetStatus();
    } else {
      alert(
        "Sorry, there was an error sending your message. Please try again."
      );
    }
  };

  const handleBackToForm = () => {
    setShowThankYou(false);
  };

  return (
    <ContactContainer>
      <HeroSection>
        <HeroTitle>Get in Touch</HeroTitle>
        <HeroSubtitle>
          Reach out to Shana with your questions, event requests, or simply to
          share a friendly hello—she looks forward to hearing from you.
        </HeroSubtitle>
      </HeroSection>

      <ContactSection>
        <ContactInfo>
          <h2>Let's Connect</h2>
          <p>
            Shana believes in the power of human connection and authentic
            conversations. Whether you're interested in her poetry, want to book
            her for an event, or simply want to share your thoughts, she's
            always open to meaningful dialogue.
          </p>

          <ContactMethod>
            <div className="icon">
              <FaEnvelope />
            </div>
            <div className="details">
              <h3>Email</h3>
              <p>shana@shanaroark.com</p>
            </div>
          </ContactMethod>

          <ContactMethod>
            <div className="icon">
              <FaPhone />
            </div>
            <div className="details">
              <h3>Phone</h3>
              <p>Available for speaking inquiries</p>
            </div>
          </ContactMethod>

          <ContactMethod>
            <div className="icon">
              <FaMapMarkerAlt />
            </div>
            <div className="details">
              <h3>Location</h3>
              <p>New York, NY</p>
            </div>
          </ContactMethod>

          <SocialLinks>
            <h3>Follow Shana</h3>
            <div className="social-grid">
              <SocialLink
                href="https://www.instagram.com/supergirlreject"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaInstagram />
              </SocialLink>
              <SocialLink
                href="https://www.tiktok.com/@supergirlreject"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaTiktok />
              </SocialLink>
              <SocialLink
                href="https://www.youtube.com/@supergirlreject"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaYoutube />
              </SocialLink>
            </div>
          </SocialLinks>
        </ContactInfo>

        {showThankYou ? (
          <ThankYouMessage>
            <ThankYouTitle>Thank You</ThankYouTitle>
            <ThankYouText>
              "Words have a way of finding their way to the right hearts at the
              right time. Your message has found its way to mine, and I'm
              grateful for the connection."
            </ThankYouText>
            <ThankYouText>
              I'll reach out to you soon with a response that's as thoughtful as
              your message. Until then, may your days be filled with the kind of
              beauty that inspires poetry.
            </ThankYouText>
            <BackButton onClick={handleBackToForm}>
              Send Another Message
            </BackButton>
          </ThankYouMessage>
        ) : (
          <ContactForm onSubmit={handleSubmit}>
            <h2>Send a Message</h2>

            <FormGroup>
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="subject">Subject</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
              >
                <option value="">Select a subject</option>
                <option value="general">General Inquiry</option>
                <option value="booking">Event Booking</option>
                <option value="poetry">Poetry Discussion</option>
                <option value="collaboration">Collaboration</option>
                <option value="other">Other</option>
              </select>
            </FormGroup>

            <FormGroup>
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell her what's on your mind..."
                required
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              <FaEnvelope /> {isSubmitting ? "Sending..." : "Send Message"}
            </SubmitButton>
          </ContactForm>
        )}
      </ContactSection>
    </ContactContainer>
  );
};

export default Contact;
